import { Component } from '@theme/component';
import { resetShimmer } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * Changes a cart line's selling plan without temporarily removing the item.
 *
 * @extends {Component}
 */
class CartItemSellingPlan extends Component {
  /** @type {HTMLSelectElement | null} */
  selector = null;

  /** @type {string} */
  #committedPlanId = '';

  /** @type {boolean} */
  #requestInFlight = false;

  connectedCallback() {
    super.connectedCallback();
    this.selector = this.querySelector('select');
    this.#committedPlanId = this.selector?.value ?? '';
  }

  /**
   * Changes the selling plan for this cart line.
   * @param {Event} event
   */
  async onChangeSellingPlan(event) {
    event.preventDefault();

    if (!this.selector || this.#requestInFlight) return;

    const key = this.dataset.key;
    const quantity = parseInt(String(this.dataset.quantity), 10);
    if (!key || !quantity) return;

    const selectedPlanId = this.selector.value;
    const planId = selectedPlanId ? parseInt(selectedPlanId, 10) : null;
    if (selectedPlanId && !planId) {
      this.selector.value = this.#committedPlanId;
      return;
    }

    const cartItemsComponents = [...document.querySelectorAll('cart-items-component')].filter(
      (component) => component instanceof HTMLElement && component.dataset.sectionId
    );
    const sectionIds = [...new Set(cartItemsComponents.map((component) => component.dataset.sectionId))];
    const cartItemRow = this.closest('.cart-items__table-row');
    const textComponent = /** @type {TextComponent | null} */ (cartItemRow?.querySelector('text-component'));
    const cartTotals = /** @type {TextComponent[]} */ (
      cartItemsComponents
        .map((component) => component.querySelector('[ref="cartTotal"]'))
        .filter((element) => element instanceof HTMLElement)
    );

    this.#requestInFlight = true;
    this.selector.disabled = true;
    this.setAttribute('aria-busy', 'true');
    this.#hideError(cartItemRow);
    textComponent?.shimmer();
    cartTotals.forEach((cartTotal) => cartTotal.shimmer());

    const marker = cartPerformance.createStartingMarker('selling-plan-change:user-action');

    try {
      const cart = await this.#changeCartLine({
        id: key,
        quantity,
        selling_plan: planId,
        sections: sectionIds,
        sections_url: window.location.pathname,
      });

      if (cart.errors || cart.status) {
        throw new Error(cart.description || cart.message || cart.errors || 'Unable to update the delivery option.');
      }

      this.#committedPlanId = selectedPlanId;
      this.dispatchEvent(
        new CartUpdateEvent(cart, key, {
          itemCount: cart.item_count,
          source: 'cart-item-selling-plan',
          action: 'selling-plan-change',
          sections: cart.sections,
        })
      );
    } catch (error) {
      this.selector.value = this.#committedPlanId;
      this.#showError(cartItemRow, error);
      console.error(error);
    } finally {
      this.#requestInFlight = false;
      this.selector.disabled = false;
      this.removeAttribute('aria-busy');
      cartItemsComponents.forEach((component) => resetShimmer(component));
      cartPerformance.measureFromMarker(marker);
    }
  }

  /**
   * Sends the cart change without passing through installed apps' fetch wrappers,
   * which can replay the line as an add and race the selling-plan update.
   * @param {Object} body
   * @param {string} body.id
   * @param {number} body.quantity
   * @param {number | null} body.selling_plan
   * @param {(string | undefined)[]} body.sections
   * @param {string} body.sections_url
   * @returns {Promise<Record<string, any>>}
   */
  #changeCartLine(body) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('POST', Theme.routes.cart_change_url);
      request.responseType = 'json';
      request.setRequestHeader('Content-Type', 'application/json');
      request.setRequestHeader('Accept', 'application/json');

      request.onload = () => {
        const cart = request.response;
        if (request.status >= 200 && request.status < 300 && cart) {
          resolve(cart);
          return;
        }

        reject(
          new Error(
            cart?.description || cart?.message || cart?.errors || 'Unable to update the delivery option.'
          )
        );
      };
      request.onerror = () => reject(new Error('Unable to update the delivery option.'));
      request.send(JSON.stringify(body));
    });
  }

  /**
   * Hides the line's existing cart error.
   * @param {Element | null} cartItemRow
   */
  #hideError(cartItemRow) {
    cartItemRow?.querySelector('.cart-items__error')?.classList.add('hidden');
  }

  /**
   * Displays a selling-plan update error on the affected cart line.
   * @param {Element | null} cartItemRow
   * @param {unknown} error
   */
  #showError(cartItemRow, error) {
    const errorContainer = cartItemRow?.querySelector('.cart-items__error');
    const errorText = cartItemRow?.querySelector('.cart-item__error-text');
    if (!(errorContainer instanceof HTMLElement) || !(errorText instanceof HTMLElement)) return;

    errorText.textContent = error instanceof Error ? error.message : 'Unable to update the delivery option.';
    errorContainer.classList.remove('hidden');
  }
}

if (!customElements.get('cart-item-selling-plan')) {
  customElements.define('cart-item-selling-plan', CartItemSellingPlan);
}
